/**
 * Live refresh (1.I-1 deliverable 19): one subscription per tab through `appEvents` that keeps
 * the sidebar's queries current. The rules are pure (`planRefresh`, `MetaVersionTracker`,
 * `treeKeysHolding`); `useLiveRefresh` applies them. Typing in a body costs nothing: a
 * `doc.changed` refetches only when its `metaVersion` differs from the last one seen.
 */
import type { AppEventV1 } from '@codex/contracts';
import { type QueryClient, type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { appEvents } from '../editor-services/events.js';
import { metaKey } from './queries.js';

export const TREE_KEYS: QueryKey = ['tree'];
export const TITLES_KEYS: QueryKey = ['titles'];
export const RECENT_KEYS: QueryKey = ['recent'];
export const TYPES_KEYS: QueryKey = ['types'];
export const CRUMB_KEYS: QueryKey = ['crumbs'];
/** The Trash list's prefix (`trashKey` in trash/trash-page.tsx; not imported, to keep the page out). */
export const TRASH_KEYS: QueryKey = ['trash'];

/** The last `metaVersion` seen per document. */
export class MetaVersionTracker {
  private readonly seen = new Map<string, number>();

  /**
   * True when `version` differs from the last one seen for the document (or, before any, from
   * `known`, a version read elsewhere); a document never seen and not known counts as changed.
   */
  changed(docId: string, version: number, known?: number): boolean {
    const last = this.seen.get(docId) ?? known;
    this.seen.set(docId, version);
    return last !== version;
  }
}

/** The cached tree levels whose answer lists `id`. */
export function treeKeysHolding(
  entries: ReadonlyArray<readonly [QueryKey, unknown]>,
  id: string,
): QueryKey[] {
  const keys: QueryKey[] = [];
  for (const [key, data] of entries) {
    if (Array.isArray(data) && data.some((node) => (node as { id?: unknown }).id === id)) {
      keys.push(key);
    }
  }
  return keys;
}

export type LiveEvent = Extract<
  AppEventV1,
  { type: 'links.changed' | 'doc.lifecycle' | 'types.changed' | 'doc.changed' }
>;

export interface PlanContext {
  readonly tracker: MetaVersionTracker;
  /** The `metaVersion` of a document the header already holds. */
  readonly knownMetaVersion: (docId: string) => number | undefined;
  readonly treeEntries: () => ReadonlyArray<readonly [QueryKey, unknown]>;
}

/** The query keys an event makes stale (each a prefix; active queries refetch at once). */
export function planRefresh(event: LiveEvent, ctx: PlanContext): QueryKey[] {
  switch (event.type) {
    case 'links.changed':
      return [TREE_KEYS];
    case 'types.changed':
      return [TYPES_KEYS];
    case 'doc.lifecycle': {
      const keys = [
        TREE_KEYS,
        TITLES_KEYS,
        RECENT_KEYS,
        TYPES_KEYS,
        metaKey(event.data.docId),
        CRUMB_KEYS,
      ];
      // A page trashed or restored elsewhere marks the Trash list stale while /trash is closed
      // (the page's own subscription covers it while open; DECISIONS 448).
      const { state } = event.data;
      return state === 'trashed' || state === 'restored' ? [...keys, TRASH_KEYS] : keys;
    }
    case 'doc.changed': {
      const { docId, metaVersion } = event.data;
      if (!ctx.tracker.changed(docId, metaVersion, ctx.knownMetaVersion(docId))) return [];
      return [
        TITLES_KEYS,
        RECENT_KEYS,
        metaKey(docId),
        CRUMB_KEYS,
        ...treeKeysHolding(ctx.treeEntries(), docId),
      ];
    }
  }
}

/** `docs.markOpened` publishes no event, so a navigation refetches Recent. */
export function planPathChange(): QueryKey[] {
  return [RECENT_KEYS];
}

/** How long after a navigation Recent is read once more (the page marks itself opened late). */
export const RECENT_SETTLE_MS = 1500;

function invalidateAll(client: QueryClient, keys: readonly QueryKey[]): void {
  for (const queryKey of keys) void client.invalidateQueries({ queryKey });
}

/** Mounted once, by the frame. */
export function useLiveRefresh(): void {
  const client = useQueryClient();
  const tracker = useRef(new MetaVersionTracker());
  useEffect(() => {
    const ctx: PlanContext = {
      tracker: tracker.current,
      knownMetaVersion: (docId) =>
        client.getQueryData<{ metaVersion?: number }>(metaKey(docId))?.metaVersion,
      treeEntries: () => client.getQueriesData({ queryKey: TREE_KEYS }),
    };
    const apply = (event: LiveEvent) => invalidateAll(client, planRefresh(event, ctx));
    const stops = [
      appEvents.subscribe('links.changed', apply),
      appEvents.subscribe('doc.lifecycle', apply),
      appEvents.subscribe('types.changed', apply),
      appEvents.subscribe('doc.changed', apply),
    ];
    return () => {
      for (const stop of stops) stop();
    };
  }, [client]);

  const pathname = useLocation({ select: (location) => location.pathname });
  const first = useRef(true);
  useEffect(() => {
    // Every pathname change (and not the first render) refetches Recent.
    void pathname;
    if (first.current) {
      first.current = false;
      return;
    }
    invalidateAll(client, planPathChange());
    const timer = setTimeout(() => invalidateAll(client, planPathChange()), RECENT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [pathname, client]);
}
